/**
 * Shows the estimated delivery range in business days, counted from today:
 * "Llegará entre el lunes 3 y el miércoles 5 de noviembre".
 * Attributes: data-desde / data-hasta (business days), data-sabado ("true" counts Saturday).
 */
class EntregaEstimada extends HTMLElement {
  static #DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
  static #MESES = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ];

  connectedCallback() {
    const desde = Number(this.dataset.desde);
    const hasta = Number(this.dataset.hasta);
    if (!Number.isFinite(desde) || !Number.isFinite(hasta) || hasta < desde) return;

    const contarSabado = this.dataset.sabado === 'true';
    const inicio = this.#sumarHabiles(desde, contarSabado);
    const fin = this.#sumarHabiles(hasta, contarSabado);
    const { DIAS, MESES } = { DIAS: EntregaEstimada.#DIAS, MESES: EntregaEstimada.#MESES };

    const textoFin = `${DIAS[fin.getDay()]} ${fin.getDate()} de ${MESES[fin.getMonth()]}`;
    const mismoMes = inicio.getMonth() === fin.getMonth();
    const textoInicio = `${DIAS[inicio.getDay()]} ${inicio.getDate()}${mismoMes ? '' : ` de ${MESES[inicio.getMonth()]}`}`;

    const b1 = document.createElement('b');
    b1.textContent = textoInicio;
    const b2 = document.createElement('b');
    b2.textContent = textoFin;
    this.replaceChildren('Llegará entre el ', b1, ' y el ', b2);
  }

  /**
   * @param {number} dias - business days to add to today
   * @param {boolean} contarSabado - whether Saturday counts as a business day
   */
  #sumarHabiles(dias, contarSabado) {
    const fecha = new Date();
    let restantes = dias;
    while (restantes > 0) {
      fecha.setDate(fecha.getDate() + 1);
      const dia = fecha.getDay();
      const habil = dia !== 0 && (contarSabado || dia !== 6);
      if (habil) restantes--;
    }
    return fecha;
  }
}

if (!customElements.get('entrega-estimada')) {
  customElements.define('entrega-estimada', EntregaEstimada);
}

/**
 * Countdown to the end of the current offer cycle ("Esta oferta se renueva en 1 h 12 min 05 s").
 * Cycles are fixed blocks of data-horas hours counted from midnight (0h, 2h, 4h… for 2 hours).
 */
class ContadorOferta extends HTMLElement {
  /** @type {number | undefined} */
  #intervalo;

  connectedCallback() {
    const horas = Number(this.dataset.horas);
    if (!Number.isFinite(horas) || horas <= 0) return;

    this.#actualizar(horas);
    this.#intervalo = window.setInterval(() => this.#actualizar(horas), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#intervalo);
  }

  /** @param {number} horas */
  #actualizar(horas) {
    const ahora = new Date();
    const ciclo = horas * 3600;
    const transcurrido = ((ahora.getHours() % horas) * 3600 + ahora.getMinutes() * 60 + ahora.getSeconds()) % ciclo;
    const resta = ciclo - transcurrido;
    const h = Math.floor(resta / 3600);
    const m = Math.floor((resta % 3600) / 60);
    const s = resta % 60;

    const tiempo = document.createElement('b');
    tiempo.textContent = `${h ? `${h} h ` : ''}${m} min ${String(s).padStart(2, '0')} s`;
    this.replaceChildren(`${this.dataset.texto ?? ''} `, tiempo);
  }
}

if (!customElements.get('contador-oferta')) {
  customElements.define('contador-oferta', ContadorOferta);
}
